import React, { useState, useEffect, useMemo } from 'react';
import { Metric } from '../components/SharedComponents';
import { getDriftConfig, setDriftConfig } from '../qlik';
import { getISOWeek } from '../utils';

const COLOR = '#1B2A4A';
const RED = '#A32D2D', GREEN = '#1A6E3C';
const MONTHS_SV = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const DOW_SV = ['mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön'];

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const fmtYM = (ym) => `${MONTHS_SV[(ym % 100) - 1]} ${Math.floor(ym / 100)}`;
const daysInMonth = (ym) => new Date(Math.floor(ym / 100), ym % 100, 0).getDate();
const nextYM = (ym) => (ym % 100 === 12) ? (Math.floor(ym / 100) + 1) * 100 + 1 : ym + 1;
// Räkenskapsår jul–jun → start = 1 juli (samma år om mån≥7, annars föregående år)
const fyStartYM = (ym) => { const y = Math.floor(ym / 100), m = ym % 100; return (m >= 7 ? y : y - 1) * 100 + 7; };

// Fasta kostnader för en kalendermånad: utfall ▸ budget ▸ fallback (fastKostnPerManad).
// months[YYYYMM] kan vara {budget,utfall} (nytt) eller ett nummer (gammalt = budget).
function resolveFast(cfg, mm) {
  const e = cfg && cfg.months && cfg.months[String(mm)];
  if (e != null) {
    if (typeof e === 'object') {
      if (e.utfall != null) return e.utfall;
      if (e.budget != null) return e.budget;
    } else if (isFinite(e)) return e;
  }
  return cfg?.fastKostnPerManad ?? 0;
}
const fyOfMonth = (mm) => { const y = Math.floor(mm / 100), m = mm % 100; return m >= 7 ? y : y - 1; }; // RÅ-startår
const fyLabel = (y) => `${y}/${String(y + 1).slice(2)}`;

// Hela kronor (dagssiffror är små → tkr blir för grovt)
const kr = (v) => (v == null || isNaN(v)) ? '–' : Math.round(v).toLocaleString('sv-SE') + ' kr';
const krSign = (v) => (v == null || isNaN(v)) ? '–' : (v >= 0 ? '+' : '−') + Math.abs(Math.round(v)).toLocaleString('sv-SE') + ' kr';
const pct = (num, den) => (den > 0 && num > 0) ? Math.round((num / den) * 100) + '%' : '–';

const sel = { fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'system-ui,sans-serif', background: 'var(--card)', color: 'var(--text)' };
const tabBtn = (active) => ({ background: active ? COLOR : 'transparent', color: active ? '#fff' : 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 20, padding: '5px 13px', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui,sans-serif' });

// Ackumulerat-graf med nollinje (klarar negativa värden, till skillnad från TrendCompare)
// monthMode = etiketter bara där label är satt (månadsgränser) + "−"/datum-fri tooltip.
function AckChart({ data, hasPrev, monthMode = false }) {
  const [hov, setHov] = useState(null);
  if (!data || !data.length) return null;
  const W = 1000, H = 280, padL = 10, padR = 10, padT = 22, padB = 26;
  const n = data.length;
  const isNum = (v) => v != null && isFinite(v);
  const vals = data.flatMap(d => hasPrev ? [d.value, d.prev] : [d.value]).filter(isNum);
  let mn = Math.min(0, ...vals), mx = Math.max(0, ...vals);
  if (mn === mx) mx = mn + 1;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const x = (i) => n === 1 ? padL + plotW / 2 : padL + (i * plotW) / (n - 1);
  const y = (v) => padT + (1 - (v - mn) / (mx - mn)) * plotH;
  const y0 = y(0);
  const pts = (k) => data.map((d, i) => ({ i, v: d[k] })).filter(p => isNum(p.v));
  const lineOf = (k) => pts(k).map((p, j) => `${j === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const areaOf = (k) => { const p = pts(k); if (p.length < 2) return ''; return `${lineOf(k)} L${x(p[p.length - 1].i).toFixed(1)},${y0.toFixed(1)} L${x(p[0].i).toFixed(1)},${y0.toFixed(1)} Z`; };
  const labelStep = Math.max(1, Math.ceil(n / 15));
  const valPts = pts('value');
  const lastP = valPts[valPts.length - 1];
  const CUR = lastP && lastP.v >= 0 ? GREEN : RED;
  const hd = hov != null ? data[hov] : null;
  const cid = 'ac' + Math.round(y0 * 7 + n); // unik nog per graf-instans för clipPath-id
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }} onMouseLeave={() => setHov(null)}>
      <defs>
        <clipPath id={`${cid}-up`}><rect x="0" y="0" width={W} height={Math.max(0, y0)} /></clipPath>
        <clipPath id={`${cid}-dn`}><rect x="0" y={y0} width={W} height={Math.max(0, H - y0)} /></clipPath>
      </defs>
      {/* nollinje */}
      <line x1={padL} y1={y0} x2={W - padR} y2={y0} stroke="var(--border)" strokeWidth="1.5" strokeDasharray="4 4" />
      <text x={padL + 2} y={y0 - 4} fontSize="10" fill="#999" fontFamily="system-ui,sans-serif">0 (break-even)</text>
      {/* area + linje: grön ovanför nollan, röd under */}
      <path d={areaOf('value')} fill={GREEN} opacity="0.10" clipPath={`url(#${cid}-up)`} />
      <path d={areaOf('value')} fill={RED} opacity="0.10" clipPath={`url(#${cid}-dn)`} />
      {hasPrev && <path d={lineOf('prev')} fill="none" stroke="#C2A878" strokeWidth="2" strokeDasharray="5 4" />}
      <path d={lineOf('value')} fill="none" stroke={GREEN} strokeWidth="2.5" clipPath={`url(#${cid}-up)`} />
      <path d={lineOf('value')} fill="none" stroke={RED} strokeWidth="2.5" clipPath={`url(#${cid}-dn)`} />
      {lastP && <circle cx={x(lastP.i)} cy={y(lastP.v)} r="3.5" fill={CUR} />}
      {data.map((d, i) => { const t = monthMode ? d.tick : (i % labelStep === 0 ? d.label : null); return t ? (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#999" fontFamily="system-ui,sans-serif">{t}</text>
      ) : null; })}
      {data.map((d, i) => {
        const xl = i === 0 ? 0 : (x(i - 1) + x(i)) / 2;
        const xr = i === n - 1 ? W : (x(i) + x(i + 1)) / 2;
        return <rect key={'h' + i} x={xl} y="0" width={Math.max(1, xr - xl)} height={H} fill="transparent" onMouseEnter={() => setHov(i)} />;
      })}
      {hd && (
        <g>
          <line x1={x(hov)} y1={padT} x2={x(hov)} y2={H - padB} stroke="var(--border)" strokeWidth="1" />
          {isNum(hd.value) && <circle cx={x(hov)} cy={y(hd.value)} r="3.5" fill={CUR} />}
          {hasPrev && isNum(hd.prev) && <circle cx={x(hov)} cy={y(hd.prev)} r="3" fill="#C2A878" />}
          <text x={Math.min(W - 160, Math.max(4, x(hov) - 70))} y={padT + 10} fontSize="11" fontWeight="600" fill="var(--text)" fontFamily="system-ui,sans-serif">
            {monthMode ? '' : 'Dag '}{hd.label}: {isNum(hd.value) ? (hd.value >= 0 ? '+' : '−') + Math.abs(Math.round(hd.value)).toLocaleString('sv-SE') + ' kr' : '–'}{hasPrev && isNum(hd.prev) ? `  ·  ifjol ${hd.prev >= 0 ? '+' : '−'}${Math.abs(Math.round(hd.prev)).toLocaleString('sv-SE')}` : ''}
          </text>
        </g>
      )}
    </svg>
  );
}

// Aggregerar daily_units → per dag { intakt, varu, lon } för en given månad (ym).
// Inkluderar ALLA enheter (även OH/ledningslöner) → äkta koncern-driftresultat.
function aggMonth(rows, ym) {
  const y = Math.floor(ym / 100), m = ym % 100;
  const prefix = `${y}-${pad(m)}-`;
  const byDay = {};
  for (const r of rows) {
    if (!r.datum.startsWith(prefix)) continue;
    const day = Number(r.datum.slice(8, 10));
    if (!byDay[day]) byDay[day] = { intakt: 0, varu: 0, lon: 0 };
    const lon = Math.abs(r.lon || 0);
    const varu = Math.abs((r.kostnad || 0) - (r.lon || 0)); // kostnad = varu + lön → varu = kostnad − lön
    byDay[day].intakt += (r.intakt || 0);
    byDay[day].lon += lon;
    byDay[day].varu += varu;
  }
  return byDay;
}

export default function RealtidDrift({ rows, isAdmin }) {
  const all = rows || [];

  // Tillgängliga månader (från data)
  const months = useMemo(() => {
    const set = new Set();
    for (const r of all) set.add(Number(r.datum.slice(0, 4)) * 100 + Number(r.datum.slice(5, 7)));
    return [...set].sort((a, b) => a - b);
  }, [all]);

  const [ym, setYm] = useState(null);
  useEffect(() => { if (months.length && ym == null) setYm(months[months.length - 1]); }, [months, ym]);

  // Konfig: fasta kostnader (kr/mån)
  const [cfg, setCfg] = useState(undefined);
  useEffect(() => { getDriftConfig().then(setCfg).catch(() => setCfg(null)); }, []);
  const fastPerManad = ym != null ? resolveFast(cfg, ym) : 0;

  // Konfig-redigering (admin): per räkenskapsår, 12 mån × {budget, utfall}
  const [cfgEdit, setCfgEdit] = useState(false);
  const [editYear, setEditYear] = useState(null);   // RÅ-startår (juli)
  const [draft, setDraft] = useState({});            // { YYYYMM: {budget, utfall} } (strängar)
  const [saving, setSaving] = useState(false);

  const fyYears = useMemo(() => {
    if (!months.length) return [];
    const a = fyOfMonth(months[0]), b = fyOfMonth(months[months.length - 1]) + 1; // +1 = budgetera nästa år
    const out = []; for (let y = a; y <= b; y++) out.push(y); return out;
  }, [months]);
  const fyMonths = (y) => { const out = []; for (let i = 0, mm = y * 100 + 7; i < 12; i++, mm = nextYM(mm)) out.push(mm); return out; };
  const loadDraft = (y) => {
    const d = {};
    for (const mm of fyMonths(y)) {
      const e = cfg?.months?.[String(mm)];
      const o = (e && typeof e === 'object') ? e : (isFinite(e) ? { budget: e } : {});
      d[mm] = { budget: o.budget != null ? String(o.budget) : '', utfall: o.utfall != null ? String(o.utfall) : '' };
    }
    setDraft(d);
  };
  const openEditor = () => { const y = fyOfMonth(ym); setEditYear(y); loadDraft(y); setCfgEdit(true); };
  const pickYear = (y) => { setEditYear(y); loadDraft(y); };
  const setField = (mm, key, val) => setDraft(p => ({ ...p, [mm]: { ...p[mm], [key]: val.replace(/[^\d]/g, '') } }));
  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const mm of fyMonths(editYear)) payload[String(mm)] = { budget: draft[mm]?.budget ?? '', utfall: draft[mm]?.utfall ?? '' };
      await setDriftConfig({ months: payload });
      const fresh = await getDriftConfig(); setCfg(fresh); setCfgEdit(false);
    } catch (e) { alert('Kunde inte spara: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const todayISO = toISO(new Date());
  const [chartMode, setChartMode] = useState('manad'); // 'manad' | 'ytd' | 'vecka' | 'ra'

  const model = useMemo(() => {
    if (ym == null) return null;
    const nDays = daysInMonth(ym);
    const fastPerDag = fastPerManad / nDays;
    const cur = aggMonth(all, ym);
    const prev = aggMonth(all, ym - 100);
    const y = Math.floor(ym / 100), m = ym % 100;

    // Fasta kostnader för valfri månad (utfall ▸ budget ▸ fallback)
    const fastForMonth = (mm) => resolveFast(cfg, mm);

    // Ackumulerat räkenskapsår (RÅ jul–jun): summera driftresultat för alla dagar
    // från RÅ-start (klampat till första datamånad) t.o.m. månaden före vald ym.
    const firstDataMonth = months.length ? months[0] : ym;
    const ytdStart = Math.max(fyStartYM(ym), firstDataMonth);
    let ytdBase = 0;
    for (let mm = ytdStart; mm < ym; mm = nextYM(mm)) {
      const ndm = daysInMonth(mm), fpd = fastForMonth(mm) / ndm, agg = aggMonth(all, mm);
      for (let d = 1; d <= ndm; d++) {
        const c = agg[d] || { intakt: 0, varu: 0, lon: 0 };
        ytdBase += c.intakt - c.varu - c.lon - fpd;
      }
    }

    const days = [];
    let ack = 0, ackPrev = 0;
    // sista faktiska dag = min(idag, månadens slut) — bara dagar med data räknas som "utfall"
    let lastActualDay = 0;
    for (let d = 1; d <= nDays; d++) {
      const iso = `${y}-${pad(m)}-${pad(d)}`;
      const hasData = !!cur[d] && iso <= todayISO;
      if (hasData) lastActualDay = d;
    }
    for (let d = 1; d <= nDays; d++) {
      const iso = `${y}-${pad(m)}-${pad(d)}`;
      const dt = fromISO(iso);
      const dow = DOW_SV[(dt.getDay() + 6) % 7];
      const c = cur[d] || { intakt: 0, varu: 0, lon: 0 };
      const isActual = d <= lastActualDay;
      const resultat = isActual ? (c.intakt - c.varu - c.lon - fastPerDag) : null;
      if (isActual) { ack += resultat; }
      // ifjol: ackumulerat samma dag-i-månad (med samma fasta kostnad → jämförbart)
      const pc = prev[d] || { intakt: 0, varu: 0, lon: 0 };
      const pres = (pc.intakt || pc.varu || pc.lon) ? (pc.intakt - pc.varu - pc.lon - fastPerDag) : -fastPerDag;
      ackPrev += pres;
      days.push({
        d, dow, weekend: (dt.getDay() === 0 || dt.getDay() === 6),
        intakt: c.intakt, varu: c.varu, lon: c.lon, fast: fastPerDag,
        resultat, ack: isActual ? ack : null, ytd: isActual ? ytdBase + ack : null, isActual,
        ackPrev,
      });
    }

    // Aggregat för utfallsperioden
    const act = days.filter(x => x.isActual);
    const sumInt = act.reduce((s, x) => s + x.intakt, 0);
    const sumVaru = act.reduce((s, x) => s + x.varu, 0);
    const sumLon = act.reduce((s, x) => s + x.lon, 0);
    const sumFast = act.length * fastPerDag;
    const sumRes = sumInt - sumVaru - sumLon - sumFast;
    const nAct = act.length || 1;

    // Break-even/dag: vilken dagsintäkt ger nollresultat?
    // Fasta dagskostnader = snittlön/dag + fast/dag (lön ~ schemalagd baskostnad).
    // Rörlig andel = varukostnad / intäkt → break-even = fasta / (1 − varuandel).
    const varuRatio = sumInt > 0 ? sumVaru / sumInt : 0;
    const fixedDaily = (sumLon / nAct) + fastPerDag;
    const breakEven = varuRatio < 0.95 ? fixedDaily / (1 - varuRatio) : null;
    const avgIntDay = sumInt / nAct;
    const avgResDay = sumRes / nAct;

    // Prognos för hela månaden (utfall + återstående dagar × snittresultat)
    const remaining = nDays - lastActualDay;
    const prognos = sumRes + remaining * avgResDay;

    // ifjol samma period (dag 1..lastActualDay) för Δ
    let prevIntSame = 0, prevResSame = 0;
    for (let d = 1; d <= lastActualDay; d++) {
      const pc = prev[d] || { intakt: 0, varu: 0, lon: 0 };
      prevIntSame += pc.intakt;
      prevResSame += (pc.intakt || pc.varu || pc.lon) ? (pc.intakt - pc.varu - pc.lon - fastPerDag) : -fastPerDag;
    }

    // RÅ-serie: ackumulerat driftresultat per månad (jul→jun), i år + ifjol-skugga.
    // Driftresultat/månad = summa över alla passerade dagar (fast kostnad accruas varje dag).
    const monthDriftActual = (mm) => {
      if (mm < firstDataMonth) return null;
      const yy = Math.floor(mm / 100), mo = mm % 100, ndm = daysInMonth(mm);
      const fpd = fastForMonth(mm) / ndm, agg = aggMonth(all, mm);
      let s = 0, any = false;
      for (let d = 1; d <= ndm; d++) {
        if (`${yy}-${pad(mo)}-${pad(d)}` > todayISO) break;
        const c = agg[d] || { intakt: 0, varu: 0, lon: 0 };
        s += c.intakt - c.varu - c.lon - fpd; any = true;
      }
      return any ? s : null;
    };
    const fyStart = fyStartYM(ym);
    let cum = 0, cumPrev = 0;
    const fySeries = [];
    for (let k = 0, mm = fyStart; k < 12; k++, mm = nextYM(mm)) {
      const md = monthDriftActual(mm), mdP = monthDriftActual(mm - 100);
      if (md != null) cum += md;
      if (mdP != null) cumPrev += mdP;
      const lbl = MONTHS_SV[(mm % 100) - 1];
      fySeries.push({ label: lbl, tick: lbl, value: md != null ? cum : null, prev: mdP != null ? cumPrev : null });
    }
    const fyHasPrev = fySeries.some(p => p.prev != null);

    // Per-datum-aggregat (för veckoupplöst RÅ); fast kostnad accruas per dag.
    const byDate = {};
    for (const r of all) {
      const k = r.datum; if (!byDate[k]) byDate[k] = { intakt: 0, varu: 0, lon: 0 };
      byDate[k].intakt += r.intakt || 0; byDate[k].lon += Math.abs(r.lon || 0); byDate[k].varu += Math.abs((r.kostnad || 0) - (r.lon || 0));
    }
    const firstDate = all.reduce((mnd, r) => r.datum < mnd ? r.datum : mnd, all[0]?.datum || todayISO);
    const driftForDate = (iso) => {
      const ymd = Number(iso.slice(0, 4)) * 100 + Number(iso.slice(5, 7));
      const c = byDate[iso] || { intakt: 0, varu: 0, lon: 0 };
      return c.intakt - c.varu - c.lon - fastForMonth(ymd) / daysInMonth(ymd);
    };
    // Vecko-serie = ackumulerat över hela RÅ (≈52 ISO-veckor) — finkornig variant av RÅ.
    const fySYear = Math.floor(fyStart / 100);
    const fySDate = fromISO(`${fySYear}-07-01`), fyEDate = fromISO(`${fySYear + 1}-06-30`);
    const startMon = new Date(fySDate); startMon.setDate(fySDate.getDate() - ((fySDate.getDay() + 6) % 7));
    let wcum = 0, wcumPrev = 0;
    const weekSeries = [];
    for (let mon = new Date(startMon), wi = 0; mon <= fyEDate; mon.setDate(mon.getDate() + 7), wi++) {
      let wHad = false, wHadPrev = false;
      for (let i = 0; i < 7; i++) {
        const d = new Date(mon); d.setDate(mon.getDate() + i);
        if (d < fySDate || d > fyEDate) continue;
        const iso = toISO(d);
        if (iso <= todayISO) { wcum += driftForDate(iso); wHad = true; }
        const py = new Date(d); py.setDate(d.getDate() - 364); const pIso = toISO(py);
        if (pIso >= firstDate && pIso <= todayISO) { wcumPrev += driftForDate(pIso); wHadPrev = true; }
      }
      const isoW = getISOWeek(mon);
      weekSeries.push({ label: `v.${isoW}`, tick: wi % 4 === 0 ? `v.${isoW}` : '', value: wHad ? wcum : null, prev: wHadPrev ? wcumPrev : null });
    }
    const weekHasPrev = weekSeries.some(p => p.prev != null);
    const weekLabel = `RÅ ${fmtYM(fyStart)} → ${fmtYM((fySYear + 1) * 100 + 6)}`;

    // Dag-serie (RÅ YTD) = ackumulerat dag för dag från RÅ-start t.o.m. senaste dag med data.
    // Finaste upplösningen; slutar vid utfallet (inte vid RÅ-slutet) → äkta "hittills".
    const lastDataDate = all.reduce((mx, r) => (r.datum > mx && r.datum <= todayISO) ? r.datum : mx, firstDate);
    const ytdEndISO = [todayISO, toISO(fyEDate), lastDataDate].sort()[0];
    let dcum = 0, dcumPrev = 0;
    const ytdSeries = [];
    for (let d = new Date(fySDate); toISO(d) <= ytdEndISO; d.setDate(d.getDate() + 1)) {
      dcum += driftForDate(toISO(d));
      const py = new Date(d); py.setDate(d.getDate() - 364); const pIso = toISO(py); // −364 = samma veckodag ifjol
      const hadPrev = pIso >= firstDate && pIso <= todayISO;
      if (hadPrev) dcumPrev += driftForDate(pIso);
      const dom = d.getDate(), mo = MONTHS_SV[d.getMonth()];
      ytdSeries.push({ label: `${dom} ${mo}`, tick: dom === 1 ? mo : '', value: dcum, prev: hadPrev ? dcumPrev : null });
    }
    const ytdHasPrev = ytdSeries.some(p => p.prev != null);
    const ytdRangeLabel = ytdSeries.length ? `${fmtYM(fyStart)} → ${ytdSeries[ytdSeries.length - 1].label}` : fmtYM(fyStart);

    return {
      nDays, fastPerDag, days, lastActualDay,
      sumInt, sumVaru, sumLon, sumFast, sumRes, nAct,
      varuRatio, breakEven, avgIntDay, avgResDay, prognos, remaining,
      prevIntSame, prevResSame,
      ytdBase, ytdFinal: ytdBase + sumRes, ytdStart,
      fySeries, fyHasPrev, fyStart,
      weekSeries, weekHasPrev, weekLabel,
      ytdSeries, ytdHasPrev, ytdRangeLabel,
      hasPrev: months.includes(ym - 100),
    };
  }, [all, ym, fastPerManad, cfg, todayISO, months]);

  if (ym == null || !model) {
    return <div className="card"><div style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen data.</div></div>;
  }

  const M = model;
  const isCurrentMonth = ym === months[months.length - 1];
  const trendData = M.days
    .filter(x => x.isActual)
    .map(x => ({ label: `${x.d}`, value: x.ack, prev: x.ackPrev }));
  const dIntPct = M.prevIntSame ? (M.sumInt - M.prevIntSame) / Math.abs(M.prevIntSame) * 100 : null;

  // Källa för vald månads fasta kostnad (utfall/budget/schablon) — slim-radens etikett
  const fe = cfg?.months?.[String(ym)];
  const fastSrcLabel = (fe && typeof fe === 'object' && fe.utfall != null) ? 'utfall'
    : (fe && ((typeof fe === 'object' && fe.budget != null) || isFinite(fe))) ? 'budget'
      : 'schablon';

  // Kombinerad ackumulerat-graf: fyra lägen (månad / RÅ YTD / RÅ-vecka / RÅ-månad)
  const fyEnd = (Math.floor(M.fyStart / 100) + 1) * 100 + 6;
  const CHART = {
    vecka: { data: M.weekSeries, hasPrev: M.weekHasPrev, monthMode: true,
      title: `Ackumulerat — per vecka (${M.weekLabel})`,
      note: `Löpande resultat över hela räkenskapsåret, per ISO-vecka (≈52 v). Slutpunkten = ackumulerat just nu (${krSign(M.ytdFinal)}). ${M.weekHasPrev ? 'Streckad = föregående räkenskapsår (begränsat av historik från dec 2024).' : 'Ifjol-skugga visas när historik finns.'}` },
    manad: { data: trendData, hasPrev: M.hasPrev, monthMode: false,
      title: `Ackumulerat — ${fmtYM(ym)}`,
      note: `Löpande summa av dagligt driftresultat. ${M.hasPrev ? 'Streckad = ifjol samma månad. ' : ''}Nollinjen = break-even för månaden.` },
    ytd: { data: M.ytdSeries, hasPrev: M.ytdHasPrev, monthMode: true,
      title: `Ackumulerat — RÅ YTD (${M.ytdRangeLabel})`,
      note: `Löpande resultat dag för dag sedan räkenskapsårets start. Slutpunkten = ackumulerat just nu (${krSign(M.ytdFinal)}). ${M.ytdHasPrev ? 'Streckad = samma dagar ifjol (−364 dagar → samma veckodag).' : 'Ifjol-skugga visas när historik finns.'}` },
    ra: { data: M.fySeries, hasPrev: M.fyHasPrev, monthMode: true,
      title: `Ackumulerat — räkenskapsår ${fmtYM(M.fyStart)} → ${fmtYM(fyEnd)}`,
      note: `Löpande resultat över hela räkenskapsåret (jul→jun), per månad. Slutpunkten = ackumulerat just nu (${krSign(M.ytdFinal)}). ${M.fyHasPrev ? 'Streckad = föregående räkenskapsår (begränsat av historik från dec 2024).' : 'Ifjol-skugga visas när historik finns.'}` },
  }[chartMode];

  return (
    <>
      {/* Månadsväljare */}
      <div className="card" style={{ padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontFamily: 'system-ui,sans-serif', color: 'var(--text-secondary)' }}>Månad</span>
        <select style={sel} value={ym} onChange={e => setYm(Number(e.target.value))}>
          {[...months].reverse().map(m => <option key={m} value={m}>{fmtYM(m)}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: COLOR, fontFamily: 'system-ui,sans-serif' }}>
          {isCurrentMonth ? `Realtid · t.o.m. dag ${M.lastActualDay}` : 'Avslutad månad'}
        </div>
      </div>

      {/* KPI:er */}
      <div className="grid-4">
        <Metric
          label="Ackumulerat RÅ — just nu"
          value={krSign(M.ytdFinal)}
          sub={`Sedan ${fmtYM(M.ytdStart)} (räkenskapsår)`}
          trend={M.ytdFinal >= 0 ? 'up' : 'down'}
        />
        <Metric
          label={`Ackumulerat — ${fmtYM(ym)}`}
          value={krSign(M.sumRes)}
          sub={M.hasPrev ? `Ifjol samma period ${krSign(M.prevResSame)}` : `${M.nAct} dagar`}
          trend={M.sumRes >= 0 ? 'up' : 'down'}
        />
        <Metric
          label="Break-even / dag"
          value={M.breakEven != null ? kr(M.breakEven) : '–'}
          sub={M.breakEven != null ? `Snittintäkt nu ${kr(M.avgIntDay)} (${M.avgIntDay >= M.breakEven ? '+' : ''}${kr(M.avgIntDay - M.breakEven)})` : 'för lite data'}
          trend={M.breakEven != null && M.avgIntDay >= M.breakEven ? 'up' : 'down'}
        />
        <Metric
          label={isCurrentMonth ? 'Prognos hela månaden' : 'Snitt resultat / dag'}
          value={isCurrentMonth ? krSign(M.prognos) : krSign(M.avgResDay)}
          sub={isCurrentMonth ? `${M.remaining} dagar kvar · snitt ${krSign(M.avgResDay)}/dag` : `Intäkt ${kr(M.sumInt)}`}
          trend={(isCurrentMonth ? M.prognos : M.avgResDay) >= 0 ? 'up' : 'down'}
        />
      </div>

      {/* Fasta kostnader — kompakt slim-rad; redigeras per RÅ (budget/utfall per månad) */}
      {!cfgEdit ? (
        <div className="card" style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontFamily: 'system-ui,sans-serif', fontSize: 13 }}>
          <span style={{ fontWeight: 600, color: COLOR }}>Fasta kostnader</span>
          <span style={{ color: 'var(--text-secondary)' }}>{fmtYM(ym)}: <strong style={{ color: 'var(--text)' }}>{kr(fastPerManad)}</strong>/mån → {kr(M.fastPerDag)}/dag · {fastSrcLabel}</span>
          {!fastPerManad && <span style={{ color: RED, fontSize: 12 }}>⚠ ej satt</span>}
          <div style={{ flex: 1 }} />
          {isAdmin && <button onClick={openEditor} style={{ ...sel, cursor: 'pointer', padding: '4px 12px', color: COLOR }}>Redigera budget/utfall</button>}
        </div>
      ) : (
        <div className="card" style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, color: COLOR, fontFamily: 'system-ui,sans-serif', fontSize: 14 }}>Fasta kostnader — räkenskapsår</span>
            <select style={sel} value={editYear ?? ''} onChange={e => pickYear(Number(e.target.value))}>
              {fyYears.map(y => <option key={y} value={y}>{fyLabel(y)}</option>)}
            </select>
            <div style={{ flex: 1 }} />
            <button onClick={saveCfg} disabled={saving} style={{ ...sel, cursor: 'pointer', padding: '5px 16px', background: COLOR, color: '#fff', border: 'none' }}>{saving ? 'Sparar…' : 'Spara'}</button>
            <button onClick={() => setCfgEdit(false)} style={{ ...sel, cursor: 'pointer', padding: '5px 12px' }}>Avbryt</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr 1fr', gap: '3px 12px', fontFamily: 'system-ui,sans-serif', fontSize: 12.5, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Månad</span>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Budget kr/mån</span>
            <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Utfall kr/mån</span>
            {fyMonths(editYear).map(mm => (
              <React.Fragment key={mm}>
                <span style={{ padding: '2px 0' }}>{fmtYM(mm)}</span>
                <input inputMode="numeric" placeholder="—" style={{ ...sel, padding: '4px 8px' }} value={draft[mm]?.budget ?? ''} onChange={e => setField(mm, 'budget', e.target.value)} />
                <input inputMode="numeric" placeholder="—" style={{ ...sel, padding: '4px 8px' }} value={draft[mm]?.utfall ?? ''} onChange={e => setField(mm, 'utfall', e.target.value)} />
              </React.Fragment>
            ))}
          </div>
          <div className="note" style={{ marginTop: 8 }}>
            Budgetera fasta kostnader per månad (sommar lågt, vinter högt). Fyll i <strong>utfall</strong> i efterhand — utfall används när det finns, annars budget. Sprids jämnt per dag. Hyra/försäkring/avskrivning m.m. som inte är varu-/lönekostnad (hämtas ej från Qlik). Lagras per räkenskapsår.
          </div>
        </div>
      )}

      {/* Ackumulerat — växla månad / RÅ YTD / RÅ-vecka / RÅ-månad */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>{CHART.title}</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => setChartMode('manad')} style={tabBtn(chartMode === 'manad')}>Månad</button>
            <button onClick={() => setChartMode('ytd')} style={tabBtn(chartMode === 'ytd')}>RÅ YTD</button>
            <button onClick={() => setChartMode('vecka')} style={tabBtn(chartMode === 'vecka')}>RÅ / vecka</button>
            <button onClick={() => setChartMode('ra')} style={tabBtn(chartMode === 'ra')}>RÅ / månad</button>
          </div>
        </div>
        {CHART.hasPrev && (
          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end', marginBottom: 2, fontFamily: 'system-ui,sans-serif', fontSize: 11, color: 'var(--text-secondary)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 3, borderRadius: 2, background: GREEN }} />I år</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 3, borderRadius: 2, background: '#C2A878' }} />Ifjol{chartMode === 'ra' ? ' (RÅ)' : ''}</span>
          </div>
        )}
        <AckChart data={CHART.data} hasPrev={CHART.hasPrev} monthMode={CHART.monthMode} />
        <div className="note">{CHART.note}</div>
      </div>

      {/* Daglig tabell */}
      <div className="card">
        <div className="card-title">Daglig drift — {fmtYM(ym)}</div>
        <div className="period-table">
          <div className="period-header period-row-9">
            <span>Dag</span><span>Intäkter</span><span>Varukost.</span><span>Löner</span><span>Personal%</span><span>Fast</span><span>Resultat</span><span>Ack. mån</span><span>Ack. RÅ</span>
          </div>
          {M.days.map(x => (
            <div key={x.d} className="period-row period-row-9" style={{ opacity: x.isActual ? 1 : 0.4, background: x.weekend ? 'rgba(27,42,74,0.04)' : 'transparent' }}>
              <span>{x.d}/{ym % 100} <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>{x.dow}</span></span>
              <span>{x.isActual && x.intakt ? kr(x.intakt) : '–'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{x.isActual && x.varu ? '−' + Math.round(x.varu).toLocaleString('sv-SE') : '–'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{x.isActual && x.lon ? '−' + Math.round(x.lon).toLocaleString('sv-SE') : '–'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{x.isActual ? pct(x.lon, x.intakt) : '–'}</span>
              <span style={{ color: 'var(--text-secondary)' }}>−{Math.round(x.fast).toLocaleString('sv-SE')}</span>
              <span style={{ fontWeight: 600, color: x.resultat == null ? 'var(--text-secondary)' : x.resultat >= 0 ? GREEN : RED }}>{x.resultat == null ? '–' : krSign(x.resultat)}</span>
              <span style={{ fontWeight: 600, color: x.ack == null ? 'var(--text-secondary)' : x.ack >= 0 ? GREEN : RED }}>{x.ack == null ? '–' : krSign(x.ack)}</span>
              <span style={{ fontWeight: 700, color: x.ytd == null ? 'var(--text-secondary)' : x.ytd >= 0 ? GREEN : RED }}>{x.ytd == null ? '–' : krSign(x.ytd)}</span>
            </div>
          ))}
          <div className="period-row period-row-9" style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
            <span>SUMMA ({M.nAct} d)</span>
            <span>{kr(M.sumInt)}{dIntPct != null && <span style={{ fontSize: 11, fontWeight: 400, color: dIntPct >= 0 ? GREEN : RED }}> {dIntPct >= 0 ? '+' : ''}{dIntPct.toFixed(0)}%</span>}</span>
            <span style={{ color: 'var(--text-secondary)' }}>−{Math.round(M.sumVaru).toLocaleString('sv-SE')}</span>
            <span style={{ color: 'var(--text-secondary)' }}>−{Math.round(M.sumLon).toLocaleString('sv-SE')}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{pct(M.sumLon, M.sumInt)}</span>
            <span style={{ color: 'var(--text-secondary)' }}>−{Math.round(M.sumFast).toLocaleString('sv-SE')}</span>
            <span style={{ color: M.sumRes >= 0 ? GREEN : RED }}>{krSign(M.sumRes)}</span>
            <span style={{ color: M.sumRes >= 0 ? GREEN : RED }}>{krSign(M.sumRes)}</span>
            <span style={{ color: M.ytdFinal >= 0 ? GREEN : RED }}>{krSign(M.ytdFinal)}</span>
          </div>
        </div>
        <div className="note">
          Resultat/dag = Intäkter − Varukostnad − Löner − Fast. <strong>Personal%</strong> = löner / intäkter. <strong>Ack. mån</strong> = löpande inom månaden · <strong>Ack. RÅ</strong> = löpande sedan {fmtYM(M.ytdStart)} (räkenskapsår jul–jun). Löner & varukostnad från Qlik (alla resultatenheter inkl. OH). Framtida dagar dämpade. Ex. moms.
        </div>
      </div>
    </>
  );
}
